import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { workFlash } from '../data/workFlash';
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * Full-screen portfolio flash: fades into view once, then hard-cuts through the work on a quick loop
 * (no transition between images). Only cycles while on screen; reduced motion shows a still frame.
 * The pictures drift inside the frame like a window (parallax); the footer below handles the unveil.
 */
export function WorkFlash() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  // Starts blurring in as soon as the frame's top edge peeks over the bottom of the screen
  const shown = useInView(ref, {
    once: true,
    amount: 0,
    margin: '0px 0px -4% 0px'
  });
  const onScreen = useInView(ref, {
    amount: 0.1
  });
  const [index, setIndex] = useState(0);
  const {
    images,
    interval
  } = workFlash;

  // Across the frame's whole trip through the screen, the pictures drift the opposite way to the scroll
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['-12%', '12%']);

  // Preload so every cut is instant
  useEffect(() => {
    images.forEach(({
      src
    }) => {
      const img = new Image();
      img.src = src;
    });
  }, [images]);
  useEffect(() => {
    if (!onScreen || reduce) return;
    const id = window.setInterval(() => setIndex(i => (i + 1) % images.length), interval);
    return () => window.clearInterval(id);
  }, [onScreen, reduce, images.length, interval]);
  return <section ref={ref} aria-label="A glimpse of our work" className="relative h-[70vh] min-h-[364px] w-full overflow-hidden bg-white" data-id="element-224" data-mp="3sz6i3hr5fu7h">
      {/* Window parallax: the frame scrolls with the page while the pictures behind it lag, as if they
          sit deep beyond the glass. The layer is taller than the frame so its edges never show. */}
      <motion.div className="absolute inset-x-0 -inset-y-[16%] will-change-transform" style={{
      y: parallaxY
    }} data-id="element-225" data-mp="39cd2vs3tcqbx">
      <motion.div className="absolute inset-0"
      // Blurs into view like the rest of the page: soft and a touch zoomed, settling sharp
      initial={reduce ? false : {
        opacity: 0,
        filter: 'blur(24px)',
        scale: 1.04
      }} animate={shown ? {
        opacity: 1,
        filter: 'blur(0px)',
        scale: 1
      } : undefined} transition={{
        opacity: {
          duration: 0.9,
          ease: EASE
        },
        filter: {
          duration: 1.1,
          ease: EASE
        },
        scale: {
          duration: 1.3,
          ease: EASE
        }
      }} data-id="element-226" data-mp="3ee2cd3be0vxw">
        {/* All frames stay mounted and decoded; only the active one is visible — a hard cut */}
        {images.map((img, i) => <img key={img.src} src={img.src} alt={i === index ? img.alt : ''} aria-hidden={i !== index} decoding="sync" className="absolute inset-0 h-full w-full object-cover [image-rendering:high-quality]" style={{
          visibility: i === index ? 'visible' : 'hidden'
        }} data-id="element-227" data-mp="excexsg83zvc" data-mpi={i} />)}
      </motion.div>
      </motion.div>
    </section>;
}